import { Col, DatePicker, Form, InputNumber, Row, Select } from "antd";
import dayjs from "dayjs";
import MultiSelectTag from "../../../components/multi-select-tag";
import {
  APWorkflowStatusTab,
  filterStatusTabCompletedOptions,
  filterStatusTabMatchedOptions,
  filterStatusTabReviewOptions,
  matchedFlagOptions,
  matchedTeamOptions,
} from "../../../constants/ap-workflow";
import { CheckOptions, DATE_FORMAT } from "../../../constants/common";
import { ISuppliersByNameResult } from "../../../interfaces/supplier";

interface FilterApWorkflowProps {
  suppliers: any[];
  apOwners: any[];
  status: string;
  isShowReview?: boolean;
  masterData?: any;
}

const FilterApWorkflow: React.FC<FilterApWorkflowProps> = ({
  suppliers,
  apOwners,
  status,
  isShowReview,
  masterData,
}) => {
  let statusFilers: any = [];
  if (status === APWorkflowStatusTab.REVIEW) {
    statusFilers = filterStatusTabReviewOptions;
  }

  if (status === APWorkflowStatusTab.MATCHED) {
    statusFilers = filterStatusTabMatchedOptions;
  }

  if (status === APWorkflowStatusTab.COMPLETED) {
    statusFilers = filterStatusTabCompletedOptions;
  }

  return (
    <>
      <Row gutter={16}>
        <h3 style={{ marginTop: 0, paddingLeft: 8 }}>Supplier Invoice Records</h3>
        <>
          <Col span={24}>
            <Form.Item label="Supplier Code" name="sirSupplierCodes">
              <MultiSelectTag
                allowClear
                showSearch
                placeholder="All"
                options={suppliers.map((item: ISuppliersByNameResult) => ({
                  value: item.code,
                  label: `${item.code} - ${item.label}`,
                }))}
                tagDisplay="value"
              />
            </Form.Item>
          </Col>
          <Form.Item shouldUpdate noStyle>
            {({ getFieldValue }) => (
              <>
                <Col span={12}>
                  <Form.Item label="From Total Cost" name="sirAmountFrom">
                    <InputNumber max={getFieldValue("sirAmountTo")} style={{ width: "100%" }} placeholder="From" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="To Total Cost" name="sirAmountTo">
                    <InputNumber min={getFieldValue("sirAmountFrom") || 1} style={{ width: "100%" }} placeholder="To" />
                  </Form.Item>
                </Col>
              </>
            )}
          </Form.Item>
        </>
        <h3 style={{ marginTop: 0, paddingLeft: 8 }}>Expected Supplier Payment</h3>
        <>
          <Col span={24}>
            <Form.Item label="Supplier Code" name="espSupplierCodes">
              <MultiSelectTag
                allowClear
                showSearch
                placeholder="All"
                options={suppliers.map((item: ISuppliersByNameResult) => ({
                  value: item.code,
                  label: `${item.code} - ${item.label}`,
                }))}
                tagDisplay="value"
              />
            </Form.Item>
          </Col>
          <Form.Item shouldUpdate noStyle>
            {({ getFieldValue }) => (
              <>
                <Col span={12}>
                  <Form.Item label="From Total Cost" name="espAmountFrom">
                    <InputNumber max={getFieldValue("espAmountTo")} style={{ width: "100%" }} placeholder="From" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="To Total Cost" name="espAmountTo">
                    <InputNumber min={getFieldValue("espAmountFrom") || 1} style={{ width: "100%" }} placeholder="To" />
                  </Form.Item>
                </Col>
              </>
            )}
          </Form.Item>

          <Form.Item shouldUpdate noStyle>
            {({ getFieldValue }) => (
              <>
                <Col span={12}>
                  <Form.Item label="Due Date From" name="espDateFrom">
                    <DatePicker
                      format={DATE_FORMAT}
                      maxDate={dayjs(getFieldValue("espDateTo"), DATE_FORMAT)}
                      style={{ width: "100%" }}
                      placeholder="From"
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="Due Date To" name="espDateTo">
                    <DatePicker
                      format={DATE_FORMAT}
                      minDate={dayjs(getFieldValue("espDateFrom"), DATE_FORMAT)}
                      style={{ width: "100%" }}
                      placeholder="To"
                    />
                  </Form.Item>
                </Col>
              </>
            )}
          </Form.Item>
        </>

        {[APWorkflowStatusTab.READY_TO_EXPORT, APWorkflowStatusTab.EXPORTED, APWorkflowStatusTab.COMPLETED].includes(
          status as APWorkflowStatusTab
        ) && (
          <>
            <h3 style={{ marginTop: 0, paddingLeft: 8 }}>Nominal Code</h3>
            <Col span={24}>
              <Form.Item label="Debit" name="debits">
                <MultiSelectTag
                  allowClear
                  showSearch
                  placeholder="All"
                  options={(masterData?.accountDebits ?? []).map((item: any) => ({
                    value: item.code,
                    label: item.label,
                  }))}
                  tagDisplay="value"
                />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item label="Credit" name="credits">
                <MultiSelectTag
                  allowClear
                  showSearch
                  placeholder="All"
                  options={(masterData?.accountCredits ?? []).map((item: any) => ({
                    value: item.code,
                    label: item.label,
                  }))}
                  tagDisplay="value"
                />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item label="Unders & Overs" name="underOvers">
                <MultiSelectTag
                  allowClear
                  showSearch
                  placeholder="All"
                  options={(masterData?.autosMatch ?? []).map((item: any) => ({
                    value: item.code,
                    label: item.label,
                  }))}
                  tagDisplay="value"
                />
              </Form.Item>
            </Col>
          </>
        )}

        <>
          {isShowReview ? (
            <Col span={24}>
              <Form.Item label="Review" name="isReviewed">
                <Select allowClear showSearch placeholder="All" options={CheckOptions} />
              </Form.Item>
            </Col>
          ) : (
            <></>
          )}

          <Col span={24}>
            <Form.Item label="Status" name="statuses">
              <MultiSelectTag allowClear showSearch placeholder="All" options={statusFilers} />
            </Form.Item>
          </Col>

          <Col span={24}>
            <Form.Item label="Flag" name="flags">
              <MultiSelectTag allowClear showSearch placeholder="All" options={matchedFlagOptions} />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item label="AP Owner" name="apOwners">
              <MultiSelectTag
                allowClear
                showSearch
                placeholder="All"
                options={apOwners.map((item) => ({
                  value: item.id,
                  label: `${item.firstName} ${item.lastName}`,
                }))}
              />
            </Form.Item>
          </Col>

          <Col span={24}>
            <Form.Item label="Team" name="teams">
              <MultiSelectTag allowClear showSearch placeholder="All" options={matchedTeamOptions} />
            </Form.Item>
          </Col>
        </>
      </Row>
    </>
  );
};

export default FilterApWorkflow;
